import React, { useEffect, useState } from "react";
import { db, pay } from "../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { Button, Flex, Modal, Progress } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { CheckCircleOutlined } from "@ant-design/icons";
import { stripeAmountToChfs } from "../utils";

export interface MovieType {
  id: string;
  name: string;
  details?: string;
  status: string;
  amount: number;
  goal?: number;
  donation?: number;
  date?: Dayjs;
  description?: string;
  pseudo: string;
  image?: string;
  detailsProj?: string;
  emails?: string[];
}

const Movies: React.FC = () => {
  const { user } = useAuth();
  const [movies, setMovies] = useState<MovieType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedMovieId, setSelectedMovieId] = useState<string>();

  useEffect(() => {
    if (!user) return;
    const collectionRef = collection(db, 'movies');
    const validatedQuery = query(collectionRef, where('status', '==', 'validated'));
    const unsubscribe = onSnapshot(validatedQuery, (snapshot) => {
      const documents = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          details: data.details,
          status: data.status,
          amount: data.amount,
          goal: data.goal,
          date: data.date? dayjs(data.date): undefined,
          description: data.description,
          pseudo: data.pseudo,
          image: data.image,
          detailsProj: data.detailsProj,
        }
    });

      const sortedMovies = documents.reduce<{
        programmed: MovieType[];
        financing: MovieType[];
        others: MovieType[];
      }>(
        (acc, movie) => {
          if (movie.goal && movie.amount < movie.goal) {
            acc.financing.push(movie);
          }
          else if (movie.date) {
            if (dayjs().startOf('day').unix() <= dayjs(movie.date).startOf('day').unix()) {
              acc.programmed.push(movie);
            }
          }
          else if (movie.goal) {
            acc.others.push(movie);
          }
          return acc;
        },
        { programmed: [], financing: [], others: [] }
      );
      
      sortedMovies.programmed.sort((a, b) => (a.date?.isBefore(b.date) ? -1 : 1)); // Sort by earliest date
      sortedMovies.financing.sort((a, b) => {
        return ((b.amount / b.goal!) * 100) - ( (a.amount / a.goal!) * 100); // Sort by goal percentage
      });
      sortedMovies.others.sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
      
      setMovies([...sortedMovies.programmed, ...sortedMovies.others, ...sortedMovies.financing]);
    });

    return () => unsubscribe();
  }, [user]);

  const participate = async (movieId: string, userId: string, donate: boolean) => {
    setLoading(true);
    await pay(movieId, userId, donate);
    setLoading(false);
  }

  return (
    <div className="flex flex-col gap-2">
      {movies.length > 0 && user && movies.map((movie, i) =>
      (i === 0 && (movie.amount >= movie.goal!)) ? 
        <div className="flex w-full max-w-md flex-row pt-4 pb-6 gap-3" key={movie.id}>
          <img className="w-1/2 h-auto object-cover" src={movie.image} alt={movie.name}/>
          <div className="flex flex-col gap-2 w-1/2">
            <div className="flex flex-col">
              <b className="text-red-500">À l'affiche :</b>
              <b>{movie.name}</b>
              <p className="italic">{movie.details}</p>
              {movie.date && <b>{`${movie.date.format("DD/MM/YYYY")}`}</b>}
              {movie.detailsProj && <b>{movie.detailsProj}</b>}
            </div>
            {movie.description && 
              <div>
                <p className="italic text-gray-400">{movie.description}</p>
                <p>{movie.pseudo}</p>
              </div>}
          </div>
        </div>
      :
        <div key={movie.id} className="flex-col">
          <div className="flex flex-col md:flex-row md:items-center md:gap-2">
            <div className="flex flex-col">
              <div className="flex flex-wrap">
                <b className="whitespace-nowrap mr-1">{movie.name}</b>
                {movie.details && <p className="px-1 italic">/</p>}
                <p className="italic">{movie.details}</p>
              </div>
              {movie.description && 
                <div className="w-full md:w-96 space-x-1">
                  <span className="italic text-gray-400">{movie.description}</span>
                  <span>{movie.pseudo}</span>
                </div>}
            </div>
            <div className="flex flex-wrap items-center gap-3 py-2">
              <Flex className="w-full max-w-[220px] md:w-[180px]">
                <Progress
                  percent={movie.amount / movie.goal! * 100}
                  strokeColor="white"
                  trailColor="gray"
                  format={() => null}
                />
              </Flex>
              {(movie.amount < movie.goal!) &&  <b className="whitespace-nowrap mr-2">{stripeAmountToChfs(movie.amount) || 0}/{stripeAmountToChfs(movie.goal)}</b>}
              {((movie.amount <  movie.goal!)) && 
                <div className="w-fit">
                  <Button size="small" ghost onClick={() => setSelectedMovieId(movie.id)}>
                    <p>Financer</p>
                  </Button>
                  <Modal
                    title="Financer un film"
                    open={!!selectedMovieId}
                    confirmLoading={loading}
                    onCancel={() => setSelectedMovieId(undefined)}
                    centered
                    footer={
                      <div className="flex justify-start gap-3 pt-2">
                        <Button
                          key="yes"
                          type="primary"
                          loading={loading}
                          onClick={() => {
                            participate(selectedMovieId!, user.uid, true);
                            setSelectedMovieId(undefined);
                          }}
                        >
                          Oui
                        </Button>
                        <Button
                          key="no"
                          loading={loading}
                          onClick={() => {
                            participate(selectedMovieId!, user.uid, false);
                            setSelectedMovieId(undefined);
                          }}
                        >
                          Non
                        </Button>
                      </div>
                    }
                  >
                    <p>
                      Souhaitez-vous que votre participation soit reversée dans la cagnotte si
                      l'objectif de financement n'est pas atteint ?
                    </p>
                  </Modal>
                </div>
              }
              {(movie.amount >= movie.goal!) && <CheckCircleOutlined className="text-green-500" />}
              {movie.date && <b>{`${movie.date.format("DD/MM/YYYY")}`}</b>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Movies;
